import React from 'react';
import { Helmet } from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';
import '../assets/scss/argon.scss';
import '../assets/scss/font-awesome.scss';
import Header from '../components/Header';
import './layout.scss';
import Footer from '../components/Footer';
import { navigate } from '@reach/router';

import { IntlProvider } from 'react-intl';
import { getLanguageFromPath } from '../helpers';

interface LayoutProps {
  className?: string;
  keywords?: string;
  title?: string;
  fullScreen?: boolean;
  description?: string;
  messages?: any;
  ogImage?: any;
  children: JSX.Element[] | JSX.Element;
  location?: any;
  recaptcha?: boolean;
}

interface StaticQueryProps {
  site: {
    siteMetadata: {
      title: string;
      description?: string;
      siteUrl: string;
      image?: string;
      keywords?: string;
      languages: any;
    };
  };
}
const getValidLangKey = (langs: any, defaultLangKey: any, langKey: any) => {
  if (!langKey) {
    return defaultLangKey;
  }

  const currentLangKey = langs.filter((l: string) => l.startsWith(langKey));
  return currentLangKey[0] || defaultLangKey;
};

const getCurrentLangKey = (langs: any, defaultLangKey: any, url: any) => {
  const langKey = url.split('/')[1];
  return getValidLangKey(langs, defaultLangKey, langKey);
};

class Layout extends React.Component<LayoutProps, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      currentLanguage: '',
    };
  }
  componentDidMount = () => {
    this.setState({
      currentLanguage: getLanguageFromPath(window.location.pathname)
        ? getLanguageFromPath(window.location.pathname)
        : 'en',
    });
  };
  switchLanguage = (language: string) => {
    console.log('inside switchLanguage');

    this.setState(
      {
        currentLanguage: language,
      },
      () => this.languageRedirect()
    );
  };

  languageRedirect = () => {
    let url = window.location.pathname.split('/')[1];
    const language = getLanguageFromPath(window.location.pathname);
    console.log('language', language);
    if (language) {
      url = window.location.pathname.split(`/${language}/`)[1];
    }
    switch (this.state.currentLanguage) {
      case 'de':
        navigate(`/de/${url}`);
        break;
      case 'es':
        navigate(`/es/${url}`);
        break;
      case 'en':
        navigate(`/${url}`);
        break;
      default:
        navigate(`/${url}`);
    }
  };

  public render() {
    return (
      <StaticQuery
        query={graphql`
          query SiteTitleQuery {
            site {
              siteMetadata {
                title
                siteUrl
                description
                keywords
                languages {
                  defaultLangKey
                  langs
                }
              }
            }
          }
        `}
        render={(data: StaticQueryProps) => {
          const { siteMetadata } = data.site;
          const { children, messages } = this.props;

          const { langs, defaultLangKey } = data.site.siteMetadata.languages;
          const langKey = getCurrentLangKey(
            langs,
            defaultLangKey,
            this.props.location?.pathname ?? '/en'
          );

          return (
            <IntlProvider locale={langKey} messages={messages}>
              <Helmet
                htmlAttributes={{ lang: langKey }}
                title={this.props.title ? this.props.title : siteMetadata.title}
                link={[
                  {
                    rel: 'canonical',
                    key: `${siteMetadata.siteUrl}${
                      this.props.location?.pathname
                        ? this.props.location?.pathname
                        : ''
                    }`,
                    href: `${siteMetadata.siteUrl}${
                      this.props.location?.pathname
                        ? this.props.location?.pathname
                        : ''
                    }`,
                  },
                ]}
                meta={[
                  {
                    name: 'description',
                    content: this.props.description
                      ? this.props.description
                      : siteMetadata.description,
                  },
                  {
                    name: 'keywords',
                    content: this.props.keywords
                      ? this.props.keywords
                      : siteMetadata.keywords,
                  },
                  {
                    property: 'og:title',
                    content: this.props.title
                      ? this.props.title
                      : siteMetadata.title,
                  },
                  {
                    property: 'og:description',
                    content: this.props.description
                      ? this.props.description
                      : siteMetadata.description,
                  },
                  {
                    property: 'og:type',
                    content: 'website',
                  },
                  {
                    name: 'twitter:card',
                    content: 'summary',
                  },
                  {
                    name: 'twitter:site',
                    content: '@esperdev',
                  },
                  {
                    name: 'twitter:creator',
                    content: '@esperdev',
                  },
                  {
                    name: 'twitter:title',
                    content: this.props.title
                      ? this.props.title
                      : siteMetadata.title,
                  },
                  {
                    name: 'twitter:image',
                    content: this.props.ogImage,
                  },
                  {
                    name: 'twitter:description',
                    content: this.props.description
                      ? this.props.description
                      : siteMetadata.description,
                  },
                  {
                    property: 'og:image',
                    content: `${siteMetadata.siteUrl}${this.props.ogImage}`,
                  },
                ]}
              >
                <script type="application/ld+json">
                  {JSON.stringify({
                    '@context': 'http://schema.org/',
                    '@type': 'WebSite',
                    name: 'Esper',
                    url: 'https://esper.io/',
                    alternateName: this.props.title
                      ? this.props.title
                      : siteMetadata.title,
                  })}
                </script>
                {this.props.recaptcha && (
                  <script
                    src={`https://www.google.com/recaptcha/api.js?onload=onLoadCallback`}
                    async={true}
                    defer={true}
                  />
                )}
                <script src="https://www.googleoptimize.com/optimize.js?id=OPT-NN8T8PD" />
              </Helmet>
              {!this.props.fullScreen && (
                <Header
                  siteUrl={this.props.location?.pathname}
                  messages={this.props.messages}
                  currentLanguage={this.state.currentLanguage}
                  switchLanguage={this.switchLanguage}
                  siteTitle={
                    this.props.title ? this.props.title : siteMetadata.title
                  }
                />
              )}
              <div className={this.props.className}>
                {children}
                {!this.props.fullScreen && (
                  <Footer
                    messages={this.props.messages}
                    currentLanguage={this.state.currentLanguage}
                  />
                )}
              </div>
            </IntlProvider>
          );
        }}
      />
    );
  }
}

export default Layout;
